import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Library, Coffee, ShoppingBag, Footprints, Car } from "lucide-react";

const CATEGORIES = [
  { key: "libraries", label: "Libraries", Icon: Library, cls: "bg-sky-100 text-sky-800" },
  { key: "cafes", label: "Cafes", Icon: Coffee, cls: "bg-amber-100 text-amber-800" },
  { key: "malls", label: "Shopping malls", Icon: ShoppingBag, cls: "bg-pink-100 text-pink-700" },
];

const fmtDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

export default function NearbyPlaces({ hostelId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/hostels/${hostelId}/nearby`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [hostelId]);

  if (!loading && (!data?.available || CATEGORIES.every((c) => !(data.categories?.[c.key] || []).length))) return null;

  return (
    <div className="mt-10" data-testid="nearby-places-section">
      <h2 className="font-display font-bold text-2xl">Around the hostel</h2>
      <p className="text-sm text-slate-500 mt-1">Nearest study & hangout spots, with walking distance.</p>
      {loading ? (
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <div key={i} className="h-44 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          {CATEGORIES.map(({ key, label, Icon, cls }) => {
            const items = data.categories?.[key] || [];
            if (!items.length) return null;
            return (
              <div key={key} data-testid={`nearby-${key}`} className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg grid place-items-center ${cls}`}><Icon size={15} /></div>
                  <div className="font-bold text-sm">{label}</div>
                </div>
                <div className="mt-3 space-y-2.5">
                  {items.map((p, i) => (
                    <div key={i} className="flex items-start justify-between gap-2">
                      <div className="text-sm text-slate-700 leading-snug min-w-0 truncate" title={p.name}>{p.name}</div>
                      <div className="text-[11px] text-slate-500 font-semibold whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                        {p.walk_min <= 25
                          ? <><Footprints size={11} className="text-emerald-600" /> {fmtDist(p.distance_m)} · {p.walk_min} min</>
                          : <><Car size={11} className="text-sky-600" /> {fmtDist(p.distance_m)} · {p.drive_min} min</>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
